import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness.js';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import {
  encryptAndSignYjsUpdate,
  verifyAndDecryptYjsUpdate,
  SenderKeyState,
  RemoteSenderKeyState,
  EncryptedYjsUpdate,
  verifyAndDecryptYjsUpdate_NoCommit,
} from '../crypto/utils/yjsUpdateEncrypt';
import { deriveMessageKey } from '../crypto/utils/cryptoEngine';
import { toBase64, fromBase64 } from '../crypto/utils/cryptoHelper';
import { FileRoomKeyStore, LocalKeyState } from 'contexts/YjsDocumentContext';

interface LocalUserState {
  name: string;
  color: string;
}

interface ConnectorOptions {
  userColor?: string;
  userId: string;
  ed25519PrivateKey: Uint8Array;

  keyStore: FileRoomKeyStore;
  //
  isHost?: boolean; // ✅ add
  onSnapshotReceived?: () => void; // ✅ callback when snapshot is received and applied
  onRequestSenderKeys?: (filePath: string) => Promise<void>; // ✨ callback to request sender keys from backend
}

/**
 * E2EE-enabled YJS SignalR Connector
 * 
 * Key differences from standard YJS connector:
 * - Encrypts all YJS updates before sending
 * - Decrypts received updates before applying to local doc
 * - Server acts as "dumb relay" (no YCS merge)
 * - Uses sender key ratchet for encryption
 */
export class YjsSignalrConnectorE2EE {
  private _doc: Y.Doc;
  private _client: HubConnection;
  private _awareness: awarenessProtocol.Awareness;
  private _sessionId: string;
  private _filePath: string;
  private _username: string;
  private _userId: string;
  private _localUser?: LocalUserState;
  
  private _hasSnapshot = false;
  private _isHost: boolean;
  // Buffer per userId (since you currently map remote keys by userId)
  //private _pendingByUser = new Map<string, Map<number, EncryptedYjsUpdate>>();

  // 🔒 Mutex: Ensures only ONE update is being applied to Y.Doc at a time
  // This prevents race conditions when multiple updates arrive simultaneously
  private _applyMutex: Promise<void> = Promise.resolve();
  // 🔒 Global send/receive queues to serialize operations
  private _sendQueue: Promise<void> = Promise.resolve();
  private _receiveQueue: Promise<void> = Promise.resolve();
  private _sendSeq = 0;
  private _recvSeq = 0;

  // E2EE state
  private _ed25519PrivateKey: Uint8Array;
  // private _localSenderKey: SenderKeyState;
  // private _remoteSenderKeys: Map<string, RemoteSenderKeyState>;
  // private _rootChainKey: Uint8Array; // ✨ Root chain key for snapshots (never ratchets)

  private _synced: boolean = false;
  // Bir kez join olduğunda tekrar snapshot istemeyi engellemek için
  private _snapshotRequested: boolean = false;
  // Retry snapshot on detected gaps
  private _resyncInFlight: boolean = false;
  

  private _keyStore: FileRoomKeyStore;
  
  // ✨ Callback to request sender keys from backend when decrypt fails
  private _onRequestSenderKeysCallback?: (filePath: string) => Promise<void>;

  private get _roomId(): string {
    return `${this._sessionId}::${this._filePath}`;
  }

  private _getRoom() {
    return this._keyStore.getOrCreateRoom(this._sessionId, this._filePath);
  }

  private _ensureLocalKey(): LocalKeyState | null {
    const current = this._keyStore.getLocalKey(this._sessionId, this._filePath);
    if (current) return current;
    if (!this._ed25519PrivateKey) {
      console.error('[YjsConnectorE2EE] Missing ed25519PrivateKey to create local key');
      return null;
    }
    return this._keyStore.ensureLocalKey(
      this._sessionId,
      this._filePath,
      this._userId,
      this._ed25519PrivateKey
    );
  }

  private _getRemoteKey(userId: string, senderKeyId: string): RemoteSenderKeyState | undefined {
    return this._keyStore.getRemoteKey(this._sessionId, this._filePath, userId, senderKeyId);
  }

  // Callback when snapshot is received
  private _onSnapshotReceivedCallback?: () => void;

  private _onEncryptedUpdateWire = (wire: EncryptedYjsUpdate): void => {
    if (!wire || wire.filePath !== this._filePath) {
      return;
    }
    void this._onEncryptedUpdateReceived(wire);
  };

  private _onEncryptedStateSnapshotWire = (wire: EncryptedYjsUpdate): void => {
    if (!wire || wire.filePath !== this._filePath) {
      return;
    }
    void this._onEncryptedStateSnapshotReceived(wire);
  };

  constructor(
    doc: Y.Doc,
    client: HubConnection,
    sessionId: string,
    filePath: string,
    username: string,
    options: ConnectorOptions,
    isHost: boolean = false
  ) {
    this._doc = doc;
    this._client = client;
    this._sessionId = sessionId;
    this._filePath = filePath;
    this._username = username;
    this._userId = options.userId;

    this._ed25519PrivateKey = options.ed25519PrivateKey;

    this._keyStore = options.keyStore;

    console.log('═══════════════════════════════════════════════════════════════');

    this._isHost = Boolean(options.isHost ?? isHost);
    this._onSnapshotReceivedCallback = options.onSnapshotReceived;
    this._onRequestSenderKeysCallback = options.onRequestSenderKeys; // ✨ Store callback

    this._awareness = new awarenessProtocol.Awareness(this._doc);
    this._awareness.on('update', this._awarenessUpdateHandler);

    const initialColor = options.userColor ?? '#' + Math.floor(Math.random() * 16777215).toString(16);
    this.setLocalUserState(username, initialColor);

    this._doc.on('updateV2', this._yDocUpdateV2Handler as any);

    this._client.onreconnected(() => this._resetConnectionAsync());
    this._client.onclose(() => this._resetConnectionAsync());

    // Receive encrypted updates/snapshot (global events, filter by filePath)
    this._client.on('ReceiveEncryptedYjsUpdate', this._onEncryptedUpdateWire);
    this._client.on('ReceiveEncryptedStateSnapshot', this._onEncryptedStateSnapshotWire);

    // Awareness events are file-scoped
    this._client.on(`QueryAwareness_${filePath}`, this._onQueryAwareness);
    this._client.on(`UpdateAwareness_${filePath}`, this._onUpdateAwareness);

    // Start initial sync
    void this._resetConnectionAsync();

  }

  // Public: allow external triggers (e.g., when host comes online after client) to re-request snapshot
  public requestSnapshot(): Promise<void> {
    return this._requestInitialState();
  }

  public get awareness(): awarenessProtocol.Awareness {
    return this._awareness;
  }

  public get localUser(): LocalUserState | undefined {
    return this._localUser;
  }

  private get connected(): boolean {
    return this._client.state === HubConnectionState.Connected;
  }

  public destroy(): void {
    this._doc.off('updateV2', this._yDocUpdateV2Handler as any);

    this._client.off('ReceiveEncryptedYjsUpdate', this._onEncryptedUpdateWire);
    this._client.off('ReceiveEncryptedStateSnapshot', this._onEncryptedStateSnapshotWire);
    this._client.off(`QueryAwareness_${this._filePath}`, this._onQueryAwareness);
    this._client.off(`UpdateAwareness_${this._filePath}`, this._onUpdateAwareness);

    awarenessProtocol.removeAwarenessStates(this._awareness, [this._doc.clientID], this);
    
    // Reset snapshot flag so new connector can request snapshot
    this._snapshotRequested = false;
    this._hasSnapshot = false;
    this._synced = false;
  }

  public setLocalUserState(userName: string, color: string): void {
    if (!userName) {
      return;
    }

    if (!color.startsWith('#')) {
      color = `#${color}`;
    }

    const normalizedColor = color.slice(0, 7);
    this._localUser = { name: userName, color: normalizedColor };
    this._awareness.setLocalStateField('user', this._localUser);
  }
  
  /**
   * Add a remote sender key (after receiving sender key announcement)
   * After adding the key, flush any buffered updates from this user
   */
  // public async addRemoteSenderKey(userId: string, remoteSenderKey: RemoteSenderKeyState): Promise<void> {
  //   this._remoteSenderKeys.set(userId, remoteSenderKey);
  //   console.log(`[YjsConnectorE2EE] Added remote sender key for user: ${userId}`);
    
  //   // 🔥 CRITICAL: Flush buffered updates for this user
  //   console.log(`[YjsConnectorE2EE] Flushing buffered updates for user: ${userId}`);
  //   await this._flushBufferedForUser(userId);
  //   console.log(`[YjsConnectorE2EE] Buffered updates flushed for user: ${userId}`);
  // }

  /**
   * Update local sender key (after key rotation or initialization)
   */
  // public updateLocalSenderKey(senderKey: SenderKeyState): void {
  //   this._localSenderKey = senderKey;
  // }

  // ==================== YJS Update Handler ====================

  private _yDocUpdateV2Handler = async (updateMessage: Uint8Array, origin: object | undefined): Promise<void> => {
    // Only encrypt and send LOCAL changes (not remote updates)
    // origin === null → User edit (from Monaco binding)
    // origin === this → Remote update we applied (don't echo back)
    if (origin !== this) {
      const seq = ++this._sendSeq;
      console.log(`[QUEUE] outbound update geldi sıra: ${seq}`);
      try {
        this._sendQueue = this._sendQueue.then(() => this._encryptAndSendUpdate(updateMessage));
        await this._sendQueue;
      } catch (error) {
        console.error('[YjsConnectorE2EE] Failed to encrypt and send update:', error);
      }
    } else {
      console.log('🔕 [Y.Doc Update Handler] Remote update (origin === this), skipping send');
    }
  };

  // ==================== Encryption & Sending ====================

  private async _encryptAndSendUpdate(update: Uint8Array): Promise<void> {
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('📤 [GÖNDEREN TARAF] Encrypted Update Sending Started!');
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('  📄 File:', this._filePath);
    console.log('  📦 Update Size:', update.length, 'bytes');
    console.log('  👤 Username:', this._username);
    
    const encryptedUpdate = await this._encryptAndSignUpdate(update);
    if (!encryptedUpdate) {
      console.error('❌ [GÖNDEREN TARAF] Encryption failed, aborting send!');
      console.log('═══════════════════════════════════════════════════════════════');
      return;
    }

    console.log('  ✅ Update encrypted successfully!');
    console.log('    - Message Index:', encryptedUpdate.messageIndex);
    console.log('    - Ciphertext Size:', encryptedUpdate.ciphertext.length, 'chars');

    // Send via new E2EE relay API
    console.log('  📡 Sending to backend via SignalR...');
    console.log('    - Method: SendEncryptedYjsUpdate');
    console.log('    - Session:', this._sessionId);
    console.log('    - File:', this._filePath);
    
    try {
      await this._client.invoke('SendEncryptedYjsUpdate', this._sessionId, this._filePath, encryptedUpdate);
      
      console.log('✅✅✅ [GÖNDEREN TARAF] UPDATE BAŞARIYLA GÖNDERİLDİ!');
      console.log('  - File:', encryptedUpdate.filePath);
      console.log('  - Message Index:', encryptedUpdate.messageIndex);
      console.log('  - Ciphertext (first 50):', encryptedUpdate.ciphertext.substring(0, 50) + '...');
      console.log('  - Signature (first 50):', encryptedUpdate.signature.substring(0, 50) + '...');
      console.log('═══════════════════════════════════════════════════════════════');
    } catch (error) {
      console.error('❌❌❌ [GÖNDEREN TARAF] Backend\'e gönderim BAŞARISIZ!');
      console.error('  Error:', error);
      console.error('  Error Message:', error instanceof Error ? error.message : String(error));
      console.error('  Error Stack:', error instanceof Error ? error.stack : 'No stack');
      console.log('═══════════════════════════════════════════════════════════════');
    }
  }

  private async _encryptAndSignUpdate(update: Uint8Array): Promise<EncryptedYjsUpdate | null> {
    console.log('🔐 [_encryptAndSignUpdate] Starting encryption...');
    console.log('  📄 File:', this._filePath);
    console.log('  📦 Update size:', update.length, 'bytes');
    
    if (!this.connected) {
      console.error('❌ [_encryptAndSignUpdate] Not connected, update dropped');
      return null;
    }

    const localKey = this._ensureLocalKey();
    if (!localKey) {
      console.error('❌ [_encryptAndSignUpdate] No local sender key!');
      return null;
    }

    if (!localKey.chainKey) {
      console.error('❌ [_encryptAndSignUpdate] No chainKey in local sender key!');
      console.error('  Local Sender Key:', localKey);
      return null;
    }

    console.log('  ✅ Local Sender Key validated:');
    console.log('    - Chain Key Type:', typeof localKey.chainKey);
    console.log('    - Chain Key instanceof Uint8Array:', localKey.chainKey instanceof Uint8Array);
    console.log('    - Chain Key Length:', localKey.chainKey.length);
    console.log('    - Message Index:', localKey.nextMessageIndex);

    try {
      // Snapshot local key state for diagnostics
      const preLocalChainB64 = toBase64(localKey.chainKey).substring(0, 16) + '...';
      const preLocalIndex = localKey.nextMessageIndex;
      console.log('  🧾 [KEY-DBG] Pre-encrypt local sender key snippet:', preLocalChainB64, 'index:', preLocalIndex);
      // Encrypt and sign the update (this should also advance senderKey index internally)
      console.log('  🔒 Calling encryptAndSignYjsUpdate...');
      const workingLocal: LocalKeyState = {
        sessionId: localKey.sessionId,
        filePath: localKey.filePath,
        userId: localKey.userId,
        senderKeyId: localKey.senderKeyId,
        chainKey: new Uint8Array(localKey.chainKey),
        rootChainKey: new Uint8Array(localKey.rootChainKey), // ✨ Preserve rootChainKey
        nextMessageIndex: localKey.nextMessageIndex,
        ed25519PrivateKey: new Uint8Array(localKey.ed25519PrivateKey),
        ed25519PublicKey: localKey.ed25519PublicKey ? new Uint8Array(localKey.ed25519PublicKey) : undefined,
        x25519PrivateKey: localKey.x25519PrivateKey ? new Uint8Array(localKey.x25519PrivateKey) : undefined,
        x25519PublicKey: localKey.x25519PublicKey ? new Uint8Array(localKey.x25519PublicKey) : undefined,
      };
      const encryptedUpdate = await encryptAndSignYjsUpdate(
        update,
        workingLocal,
        this._username
      );
      // Persist advanced sender key back to store
      this._keyStore.setLocalKey(this._sessionId, this._filePath, workingLocal);

      // Post-encrypt local key state (should have advanced)
      const postLocalChainB64 = toBase64(workingLocal.chainKey).substring(0, 16) + '...';
      const postLocalIndex = workingLocal.nextMessageIndex;
      console.log('  🧾 [KEY-DBG] Post-encrypt local sender key snippet:', postLocalChainB64, 'index:', postLocalIndex);
      console.log('  🧾 [KEY-DBG] Encrypted message index sent:', encryptedUpdate?.messageIndex, 'senderKeyId:', encryptedUpdate?.senderKeyId);
      console.log('  ✅ Encryption successful!');
      console.log('    - Encrypted Message Index:', encryptedUpdate.messageIndex);
      
      // Send via SignalR
      console.log('📤 [ENCRYPT & SIGN] Encrypted YJS Update:');
      console.log('  📄 File:', encryptedUpdate.filePath);
      console.log('  🔢 Index:', encryptedUpdate.messageIndex);
      console.log('  🔐 Payload:', JSON.stringify({
        sessionId: encryptedUpdate.sessionId,
        filePath: encryptedUpdate.filePath,
        userId: encryptedUpdate.userId,
        username: encryptedUpdate.username,
        messageIndex: encryptedUpdate.messageIndex,
        senderKeyId: encryptedUpdate.senderKeyId,
        nonce: encryptedUpdate.nonce,
        ciphertext: encryptedUpdate.ciphertext.substring(0, 50) + '...',
        signature: encryptedUpdate.signature.substring(0, 50) + '...'
      }, null, 2));

      return encryptedUpdate;
    } catch (error) {
      console.error('❌ [_encryptAndSignUpdate] Encryption failed!');
      console.error('  Error:', error);
      console.error('  Error Message:', error instanceof Error ? error.message : String(error));
      console.error('  Error Stack:', error instanceof Error ? error.stack : 'No stack');
      throw error;
    }
  }

  // ==================== Receiving & Decryption ====================

  private _onEncryptedUpdateReceived = async (wire: EncryptedYjsUpdate): Promise<void> => {
    const seq = ++this._recvSeq;
    console.log(`[QUEUE] inbound update geldi sıra: ${seq}`);
    // Serialize all inbound updates (across all users) to avoid concurrent decrypt/apply
    this._receiveQueue = this._receiveQueue.then(() => this._handleEncryptedUpdate(wire));
    return this._receiveQueue;
  };

  private _handleEncryptedUpdate = async (wire: EncryptedYjsUpdate): Promise<void> => {
  try {
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('📥 [RECEIVE] Encrypted Update Received');
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('  📄 File:', wire.filePath);
    console.log('  👤 From User:', wire.username, '| UserID:', wire.userId);
    console.log('  🔢 Message Index:', wire.messageIndex);
    console.log('  🆔 Sender Key ID:', wire.senderKeyId);
    console.log('  ⏱️  Timestamp:', wire.timestamp);
    
    // Snapshot gate: do not apply anything until snapshot is applied
    if (!this._hasSnapshot) {
      console.log('  ⏸️  [BUFFERED] Waiting for snapshot, buffering update');
      this._bufferEncryptedUpdate(wire);

      // Proactively (re)request snapshot if we somehow missed it
      if (!this._isHost && !this._snapshotRequested && !this._resyncInFlight) {
        this._resyncInFlight = true;
        console.log('[SnapshotGate] Missing snapshot; requesting now');
        void this._requestInitialState().finally(() => {
          this._resyncInFlight = false;
        });
      }
      return;
    }

    const remoteSenderKey = this._getRemoteKey(wire.userId, wire.senderKeyId);
    if (!remoteSenderKey) {
      console.error('  ⚠️  [KEY MISSING] Remote sender key bulunamadı!');
      console.error('    → userId:', wire.userId);
      console.error('    → senderKeyId:', wire.senderKeyId);
      console.error('    → SORUN: Bu kullanıcının sender key\'i henüz announce edilmemiş veya alınmamış');
      console.error('    → ÇÖZÜM: Backend\'den key listesi talep ediliyor...');
      this._bufferEncryptedUpdate(wire);
      
      // Request sender keys immediately
      if (this._onRequestSenderKeysCallback) {
        try {
          await this._onRequestSenderKeysCallback(wire.filePath);
          console.log('    ✅ Key listesi talep edildi, update buffer\'da bekleyecek');
        } catch (err) {
          console.error('    ❌ Key listesi talep edilemedi:', err);
        }
      }
      return;
    }

    console.log('  🔑 Remote Sender Key State:');
    console.log('    - Sender Key ID:', remoteSenderKey.senderKeyId);
    console.log('    - Last Message Index:', remoteSenderKey.lastMessageIndex);
    console.log('    - Chain Key Length:', remoteSenderKey.chainKey?.length);
    console.log('    - Session ID:', remoteSenderKey.sessionId);
    console.log('    - File Path:', remoteSenderKey.filePath);
    try {
      const remoteChainB64 = remoteSenderKey.chainKey ? toBase64(remoteSenderKey.chainKey).substring(0, 16) + '...' : 'none';
      console.log('  🔑 Remote Chain Key (snippet):', remoteChainB64);
    } catch (e) {
      console.log('  🔑 Remote Chain Key (snippet): <err>');
    }
    if (wire.senderKeyId !== remoteSenderKey.senderKeyId) {
      console.warn('  ⚠️ SenderKeyId mismatch: wire=', wire.senderKeyId, 'remote=', remoteSenderKey.senderKeyId);
    }

    // strict in-order (because deriveMessageKey is sequential)
    const expected = (remoteSenderKey.lastMessageIndex ?? -1) + 1;
    console.log('  📊 Index Check:');
    console.log('    - Expected Index:', expected);
    console.log('    - Received Index:', wire.messageIndex);

    if (wire.messageIndex <= (remoteSenderKey.lastMessageIndex ?? -1)) {
      console.log('  ⏭️  [SKIPPED] Already processed (old message), skipping');
      console.log('═══════════════════════════════════════════════════════════════');
      return;
    }

    if (wire.messageIndex !== expected) {
      console.log('  ⏸️  [BUFFERED] Out of order, buffering for later');
      console.log('    - Gap detected: expected', expected, 'but got', wire.messageIndex);
      this._bufferEncryptedUpdate(wire);
      // If we are a joiner and see a gap, force a fresh snapshot to realign
      if (!this._isHost && !this._resyncInFlight) {
        this._resyncInFlight = true;
        this._snapshotRequested = false; // allow re-request
        console.log('[Resync] Gap detected, requesting fresh snapshot to realign');
        void this._requestInitialState().finally(() => {
          this._resyncInFlight = false;
        });
      }
      console.log('═══════════════════════════════════════════════════════════════');
      return;
    }

    console.log('  ✅ Update sırası doğru, decrypt ediliyor...');
    await this._applyOneInOrder(wire, remoteSenderKey);
    console.log('  ✅✅✅ UPDATE BAŞARIYLA DEŞIFRE EDILDI VE UYGULANДИ!');
    console.log('    - User:', wire.username);
    console.log('    - Index:', wire.messageIndex);

    // flush buffered contiguous updates for this user
    console.log('  🔄 Buffered update\'ler kontrol ediliyor (this user)...');
    await this._flushBufferedForUser(wire.userId, remoteSenderKey.senderKeyId);
    
    // 🔥 ALSO: Try to flush ALL users' buffers
    // This helps when remote sender keys arrive late
    console.log('  🔄 Checking ALL users\' buffered updates...');
    await this._flushAllBufferedUsers();
    console.log('═══════════════════════════════════════════════════════════════');
  } catch (error) {
    console.error('═══════════════════════════════════════════════════════════════');
    console.error('❌❌❌ [HANDLE UPDATE] KRITIK HATA!');
    console.error('═══════════════════════════════════════════════════════════════');
    console.error('  📄 File:', wire.filePath);
    console.error('  👤 From User:', wire.username, '| UserID:', wire.userId);
    console.error('  🔢 Message Index:', wire.messageIndex);
    console.error('  🆔 Sender Key ID:', wire.senderKeyId);
    console.error('  ⚠️  Error Type:', error instanceof Error ? error.constructor.name : typeof error);
    console.error('  💬 Error Message:', error instanceof Error ? error.message : String(error));
    
    if (error instanceof Error && error.message.includes('[DECRYPT-DETAIL]')) {
      console.error('  🔍 DETAYLI HATA ANALİZİ:');
      console.error('    → Decrypt işlemi sırasında hata oluştu');
      console.error('    → Yukarıdaki [DECRYPT-DETAIL] loglarına bakarak hangi adımda hata aldığını görebilirsin');
    }
    
    console.error('  📚 Stack:', error instanceof Error ? error.stack : 'No stack');
    console.error('═══════════════════════════════════════════════════════════════');
  }
};

  private _onEncryptedStateSnapshotReceived = async (wire: EncryptedYjsUpdate): Promise<void> => {
    try {
      console.log('═══════════════════════════════════════════════════════════════');
      console.log(`📸 [Snapshot][Joiner] Received snapshot`);
      console.log(`  - From: ${wire.username} (${wire.userId})`);
      console.log(`  - File: ${wire.filePath}`);
      console.log(`  - Message Index: ${wire.messageIndex}`);
      console.log(`  - Sender Key ID: ${wire.senderKeyId}`);
      console.log('═══════════════════════════════════════════════════════════════');

      const remoteSenderKey = this._getRemoteKey(wire.userId, wire.senderKeyId);
      if (!remoteSenderKey) {
        console.error('  ❌ [Snapshot] Remote sender key BULUNAMADI!');
        console.error('    → userId:', wire.userId);
        console.error('    → senderKeyId:', wire.senderKeyId);
        console.error('    → Snapshot buffer\'a alınıyor ve key talep ediliyor...');
        this._bufferEncryptedUpdate(wire);
        
        // Request sender keys
        if (this._onRequestSenderKeysCallback) {
          try {
            await this._onRequestSenderKeysCallback(wire.filePath);
            console.log('    ✅ Key listesi talep edildi');
          } catch (err) {
            console.error('    ❌ Key listesi talep edilemedi:', err);
          }
        }
        return;
      }

      console.log(`  🔐 [Snapshot] Decrypting snapshot...`);
      console.log(`    - Sender Key ID: ${remoteSenderKey.senderKeyId}`);
      console.log(`    - Last Message Index: ${remoteSenderKey.lastMessageIndex}`);
      console.log(`    - Has rootChainKey: ${!!remoteSenderKey.rootChainKey}`);

      // Keep the announced last index so we don't downgrade ratchet state
      const announcedLastIndex = typeof remoteSenderKey.lastMessageIndex === 'number'
        ? remoteSenderKey.lastMessageIndex
        : -1;
      
      // ✨ CRITICAL: Pass isSnapshot=true to use rootChainKey
      const { update: decryptedSnapshot } =
        await verifyAndDecryptYjsUpdate_NoCommit(wire, remoteSenderKey, true);
      
      console.log(`  ✅ [Snapshot] Decrypt successful | bytes=${decryptedSnapshot.length}`);

      await (this._applyMutex = this._applyMutex.then(async () => {
        console.log('  📝 [Snapshot] Applying to Y.Doc...');
        Y.applyUpdateV2(this._doc, decryptedSnapshot, this);
        console.log('  ✅ [Snapshot] Applied to Y.Doc');
      }));

      // ✨ CRITICAL FIX: Do NOT ratchet past the snapshot key for the next update.
      // Rebase chainKey from the rootChainKey using the announced last index
      // so the next expected message derives the same key as the sender.
      const targetLastIndex = Math.max(announcedLastIndex, wire.messageIndex);
      const baseChainKey = remoteSenderKey.rootChainKey ?? remoteSenderKey.chainKey;
      const chainKeyToStore = await this._advanceChainKey(baseChainKey, targetLastIndex);

      remoteSenderKey.chainKey = chainKeyToStore;
      remoteSenderKey.lastMessageIndex = targetLastIndex;
      console.log(`  🔄 [Snapshot] Remote sender key updated | prevIdx=${announcedLastIndex} -> lastIdx=${remoteSenderKey.lastMessageIndex}`);

      this._hasSnapshot = true;
      this._synced = true;
      console.log(`  ✅✅✅ [Snapshot] SNAPSHOT BAŞARIYLA UYGULANДИ!`);

      // Notify EditorContainer that snapshot is ready
      if (this._onSnapshotReceivedCallback) {
        console.log('  📞 [Snapshot] Calling onSnapshotReceived callback');
        this._onSnapshotReceivedCallback();
      }

      console.log('  🔄 [Snapshot] Flushing buffered updates after snapshot...');
      await this._flushAllBufferedUsers();
      console.log('  ✅ [Snapshot] Buffered updates flushed');
      console.log('═══════════════════════════════════════════════════════════════');

    } catch (error) {
      console.error('═══════════════════════════════════════════════════════════════');
      console.error('❌❌❌ [Snapshot] SNAPSHOT DECRYPT/APPLY HATASI!');
      console.error('═══════════════════════════════════════════════════════════════');
      console.error('  📄 File:', wire?.filePath);
      console.error('  👤 From User:', wire?.username, '| UserID:', wire?.userId);
      console.error('  🔢 Message Index:', wire?.messageIndex);
      console.error('  🆔 Sender Key ID:', wire?.senderKeyId);
      console.error('  ⚠️  Error Type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('  💬 Error Message:', error instanceof Error ? error.message : String(error));
      
      if (error instanceof Error && error.message.includes('[DECRYPT-DETAIL]')) {
        console.error('  🔍 DETAYLI HATA ANALİZİ:');
        console.error('    → Decrypt işlemi sırasında hata oluştu');
        console.error('    → Yukarıdaki [DECRYPT-DETAIL] loglarına bakarak hangi adımda hata aldığını görebilirsin');
      }
      
      console.error('  📚 Stack:', error instanceof Error ? error.stack : 'No stack');
      console.error('═══════════════════════════════════════════════════════════════');
    }
  };

  // private _processPendingUpdatesForUser(userId: string): void {
  //   const pending = this._pendingUpdates.filter(u => u.userId === userId);
  //   this._pendingUpdates = this._pendingUpdates.filter(u => u.userId !== userId);

  //   for (const update of pending) {
  //     this._onEncryptedUpdateReceived(update);
  //   }
  // }

  // ==================== Awareness (NOT Encrypted) ====================

  private _awarenessUpdateHandler = ({ added, updated, removed }: any, origin: unknown): void => {
    if (origin === this) {
      return;
    }
    const changedClients = added.concat(updated).concat(removed);
    if (changedClients.length === 0) {
      return;
    }
    

    
    const localState = this._awareness.getLocalState();
    if (localState?.selection) {
      const sel = localState.selection;
      if (sel.anchor && sel.head) {
        const anchorAbs = Y.createAbsolutePositionFromRelativePosition(sel.anchor, this._doc);
        const headAbs = Y.createAbsolutePositionFromRelativePosition(sel.head, this._doc);
      }
    }
    
    const update = awarenessProtocol.encodeAwarenessUpdate(this._awareness, changedClients);
    this._sendAwarenessUpdate(update);
  };

  private _onQueryAwareness = (): void => {
    const update = awarenessProtocol.encodeAwarenessUpdate(
      this._awareness,
      Array.from(this._awareness.getStates().keys())
    );
    this._sendAwarenessUpdate(update);
  };

  private _onUpdateAwareness = (data: string): void => {
    if (data) {
      const update = this._stringToByteArray(data);
      awarenessProtocol.applyAwarenessUpdate(this._awareness, update, this);
    }
  };

  private async _sendAwarenessUpdate(update: Uint8Array): Promise<void> {
    if (!this.connected) {
      return;
    }

    await this._client.send(
      'UpdateAwareness',
      this._sessionId,
      this._filePath,
      this._username,
      this._byteArrayToString(update)
    );
  }

  // ==================== Connection Management ====================

  private async _resetConnectionAsync(): Promise<void> {
    this._synced = false;
    this._snapshotRequested = false; // reconnect sonrası tekrar isteyebilir

    if (this.connected) {
      // Request initial state snapshot from other users
      await this._requestInitialState();
      await this._requestAndBroadcastAwareness();
    } else {
      // Offline - remove all remote awareness
      awarenessProtocol.removeAwarenessStates(
        this._awareness,
        Array.from(this._awareness.getStates().keys()).filter(client => client !== this._doc.clientID),
        this
      );
    }
  }

  private async _requestInitialState(): Promise<void> {
    if (!this.connected) return;

    // Host already has full doc state locally
    if (this._isHost) {
      this._hasSnapshot = true;
      this._synced = true;
      return;
    }

    this._hasSnapshot = false;
    this._synced = false;

    if (this._snapshotRequested) {
      return;
    }
    this._snapshotRequested = true;

    try {
      console.log(`[Snapshot][Joiner] Requesting snapshot | session=${this._sessionId} | file=${this._filePath} | connState=${this._client.state}`);
      await this._client.invoke('RequestEncryptedStateSnapshot', this._sessionId, this._filePath);
      console.log('[Snapshot][Joiner] Request sent, awaiting response');
    } catch (error) {
      console.error('[Snapshot][Joiner] ❌ Snapshot request failed:', error);
    }
  }

  private async _requestAndBroadcastAwareness(): Promise<void> {
    if (!this.connected) {
      return;
    }

    // Query awareness from others
    await this._client.send('QueryAwareness', this._sessionId, this._filePath, this._username, '');

    // Broadcast our awareness
    if (this._awareness.getLocalState() !== null) {
      const update = awarenessProtocol.encodeAwarenessUpdate(this._awareness, [this._doc.clientID]);
      await this._sendAwarenessUpdate(update);
    }
  }

  // ==================== Utility ====================

  private _byteArrayToString(bytes: Uint8Array): string {
    let result = '';
    for (let i = 0; i < bytes.length; i++) {
      result += String.fromCharCode(bytes[i]);
    }
    return result;
  }

  private _stringToByteArray(str: string): Uint8Array {
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
      bytes[i] = str.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Send encrypted state snapshot to a new user
   * Called when a new user joins the document
   */
  public async sendStateSnapshotToUser(targetConnectionId: string): Promise<void> {
    if (!this.connected) {
      console.error('[Snapshot][HostConn] ❌ Not connected, cannot send snapshot');
      return;
    }

    const localKey = this._ensureLocalKey();
    if (!localKey || !localKey.rootChainKey || !localKey.ed25519PrivateKey) {
      console.error('[Snapshot][HostConn] ❌ Missing sender key state or rootChainKey');
      return;
    }

    try {
      const stateUpdate = Y.encodeStateAsUpdateV2(this._doc);
      console.log(`[Snapshot][HostConn] Encoding doc state | bytes=${stateUpdate.length}`);

      // ✨ CRITICAL FIX: Use rootChainKey for snapshot encryption (never ratchets)
      // This ensures the client can decrypt with the original key from the announcement
      const snapshotSenderKey: SenderKeyState = {
        sessionId: localKey.sessionId,
        filePath: localKey.filePath,
        userId: localKey.userId,
        senderKeyId: localKey.senderKeyId,
        chainKey: new Uint8Array(localKey.rootChainKey), // ✨ Use root key instead of current chainKey
        nextMessageIndex: 0,
        ed25519PrivateKey: new Uint8Array(localKey.ed25519PrivateKey)
      };

      console.log(`[Snapshot][HostConn] Using ROOT chain key for snapshot encryption`);
      console.log(`  - Snapshot Message Index: 0 (always)`);
      console.log(`  - Current Local Message Index: ${localKey.nextMessageIndex} (for regular updates)`);

      const encryptedSnapshot = await encryptAndSignYjsUpdate(
        stateUpdate,
        snapshotSenderKey, // ✨ Use snapshot-specific key with root chainKey
        this._username
      );
      console.log(`[Snapshot][HostConn] Encrypted snapshot | msgIndex=${encryptedSnapshot.messageIndex} | cipherChars=${encryptedSnapshot.ciphertext.length}`);

      await this._client.invoke('SendEncryptedStateSnapshot', targetConnectionId, encryptedSnapshot);
      console.log(`[Snapshot][HostConn] ✅ Snapshot sent | target=${targetConnectionId} | msgIndex=${encryptedSnapshot.messageIndex}`);
    } catch (error) {
      console.error('[Snapshot][HostConn] ❌ Snapshot send failed:', error);
    }
  }


  // ==================== Buffering & In-Order Delivery ====================
  
  private _bufferEncryptedUpdate(wire: EncryptedYjsUpdate): void {
    this._keyStore.bufferWire(this._sessionId, this._filePath, wire);
  }

  private async _applyOneInOrder(
    wire: EncryptedYjsUpdate,
    remoteSenderKey: RemoteSenderKeyState
  ): Promise<void> {
    // 🔒 CRITICAL: Use mutex to ensure only ONE update is applied at a time
    // Without this, concurrent updates cause race conditions in Y.Doc state
    console.log(`  🔒 [MUTEX] Waiting to acquire lock for update idx=${wire.messageIndex} from ${wire.username}...`);
    await (this._applyMutex = this._applyMutex.then(async () => {
      console.log(`  🔓 [MUTEX] Lock acquired for update idx=${wire.messageIndex}`);
      console.log(`  🔐 [DECRYPT] Starting decryption...`);
      console.log(`    - Wire Message Index: ${wire.messageIndex}`);
      try {
        const beforeB64 = remoteSenderKey.chainKey ? toBase64(remoteSenderKey.chainKey).substring(0, 16) + '...' : 'none';
        console.log(`    - Remote Chain Key (before): ${beforeB64}`);
      } catch (e) {
        console.log('    - Remote Chain Key (before): <err>');
      }
      console.log(`    - Remote Last Index (before): ${remoteSenderKey.lastMessageIndex}`);
      
      try {
        const { update: decryptedUpdate, nextChainKey } =
          await verifyAndDecryptYjsUpdate_NoCommit(wire, remoteSenderKey);

        console.log(`  ✅ [DECRYPT] Decryption successful!`);
        console.log(`    - Decrypted update size: ${decryptedUpdate.length} bytes`);
        try {
          const nextB64 = nextChainKey ? toBase64(nextChainKey).substring(0, 16) + '...' : 'none';
          console.log(`    - Next Chain Key: ${nextB64}`);
        } catch (e) {
          console.log('    - Next Chain Key: <err>');
        }

        Y.applyUpdateV2(this._doc, decryptedUpdate, this);
        console.log(`  ✅ [Y.DOC] Update applied to document`);

        // Commit ratchet only after apply
        remoteSenderKey.chainKey = nextChainKey;
        remoteSenderKey.lastMessageIndex = wire.messageIndex;

        console.log(`  🔄 [RATCHET] Sender key state updated`);
        try {
          const afterB64 = remoteSenderKey.chainKey ? toBase64(remoteSenderKey.chainKey).substring(0, 16) + '...' : 'none';
          console.log(`    - Remote Chain Key (after): ${afterB64}`);
        } catch (e) {
          console.log('    - Remote Chain Key (after): <err>');
        }
        console.log(`    - Remote Last Index (after): ${remoteSenderKey.lastMessageIndex}`);
        console.log(`  ✅ [MUTEX] Update applied and lock released: ${wire.username} idx=${wire.messageIndex}`);
      } catch (decryptError) {
        console.error(`  ❌ [DECRYPT] Decryption FAILED!`);
        console.error(`    - Error:`, decryptError);
        console.error(`    - Error Type:`, decryptError instanceof Error ? decryptError.name : typeof decryptError);
        console.error(`    - Error Message:`, decryptError instanceof Error ? decryptError.message : String(decryptError));
        console.error(`    - Wire Message Index: ${wire.messageIndex}`);
        console.error(`    - Remote Last Index: ${remoteSenderKey.lastMessageIndex}`);
        console.error(`    - Expected Index: ${(remoteSenderKey.lastMessageIndex ?? -1) + 1}`);
        
        // ✨ RETRY MECHANISM: Request sender keys from backend and retry
        if (this._onRequestSenderKeysCallback) {
          console.log(`🔁 [RETRY] Backend'den key listesi isteniyor...`);
          try {
            await this._onRequestSenderKeysCallback(wire.filePath);
            console.log(`✅ [RETRY] Key listesi alındı, tekrar decrypt ediliyor...`);
            
            // Get updated remote sender key (may have been updated by key announcement handler)
            const updatedRemoteSenderKey = this._getRemoteKey(wire.userId, wire.senderKeyId);
            if (updatedRemoteSenderKey) {
              const { update: decryptedUpdate, nextChainKey } =
                await verifyAndDecryptYjsUpdate_NoCommit(wire, updatedRemoteSenderKey);
              
              Y.applyUpdateV2(this._doc, decryptedUpdate, this);
              console.log(`✅ [RETRY] Update başarıyla decrypt edildi ve uygulandı!`);
              
              // Commit ratchet
              updatedRemoteSenderKey.chainKey = nextChainKey;
              updatedRemoteSenderKey.lastMessageIndex = wire.messageIndex;
              console.log(`✅ [RETRY] Sender key state güncellendi`);
              return; // Success after retry
            } else {
              console.error(`❌ [RETRY] Remote sender key hala bulunamadı`);
            }
          } catch (retryError) {
            console.error(`❌ [RETRY] Tekrar deneme başarısız:`, retryError);
          }
        }
        
        throw decryptError;
      }
    }));
  }

  // Fast-forward chainKey by `steps` to align with a more advanced lastMessageIndex
  private async _advanceChainKey(chainKey: Uint8Array, steps: number): Promise<Uint8Array> {
    let ck = chainKey;
    for (let i = 0; i < steps; i++) {
      const { nextChainKey } = await deriveMessageKey(ck, i);
      ck = nextChainKey;
    }
    return ck;
  }

  private async _flushBufferedForUser(userId: string, senderKeyId?: string): Promise<void> {
    const room = this._getRoom();
    const senderRing = room.remotes.get(userId);
    if (!senderRing) return;

    const keysToFlush = senderKeyId ? [senderKeyId] : Array.from(senderRing.keys());
    for (const skId of keysToFlush) {
      const remoteSenderKey = senderRing.get(skId);
      if (!remoteSenderKey) continue;

      const buffered = this._keyStore.drainBuffered(this._sessionId, this._filePath, userId, skId);
      if (!buffered.length) continue;

      buffered.sort((a, b) => a.messageIndex - b.messageIndex);

      for (const wire of buffered) {
        const expected = (remoteSenderKey.lastMessageIndex ?? -1) + 1;
        if (wire.messageIndex !== expected) {
          // Still out-of-order; put it back and stop processing this sender
          this._bufferEncryptedUpdate(wire);
          break;
        }
        await this._applyOneInOrder(wire, remoteSenderKey);
      }
    }
  }

  /**
   * Try to flush buffered updates for ALL users
   * Useful when remote sender keys arrive late
   */
  private async _flushAllBufferedUsers(): Promise<void> {
    const room = this._getRoom();
    const userIds = Array.from(room.pending.keys());
    if (userIds.length === 0) return;

    console.log(`  🔄 Flushing buffers for ${userIds.length} users...`);
    for (const userId of userIds) {
      await this._flushBufferedForUser(userId);
    }
  }

}
